import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BoxedSliderField } from "web_myplann/Inspiration/Components/BoxedSliderField";
import { EarlyFreedomScenarioCard } from "web_myplann/Inspiration/Components/EarlyFreedomScenarioCard";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import {
	EARLY_FREEDOM_SAVING_MAX,
	EARLY_FREEDOM_SAVING_MIN,
	EARLY_FREEDOM_SAVING_STEP,
	EARLY_FREEDOM_SCENARIO_LABELS,
} from "web_myplann/Inspiration/constants";
import { useEarlyFreedomYearsLabel } from "web_myplann/Inspiration/Hooks/useEarlyFreedomYearsLabel";
import type {
	EarlyFreedomContext,
	EarlyFreedomScenarioKey,
} from "web_myplann/Inspiration/Utils/earlyFreedomMath";
import { getEarlyFreedomScenarioRatePercent } from "web_myplann/Inspiration/Utils/earlyFreedomMath";

const R = Resources.Inspiration.EarlyFreedom;

type Props = {
	context: EarlyFreedomContext;
	selectedScenario: EarlyFreedomScenarioKey;
	onChange: (value: number) => void;
	onSelectScenario: (scenario: EarlyFreedomScenarioKey) => void;
};

export const EarlyFreedomPaceSection: FC<Props> = ({
	context,
	selectedScenario,
	onChange,
	onSelectScenario,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const formatYears = useEarlyFreedomYearsLabel();

	return (
		<InspirationSection title={t(R.paceTitle)} gap={1.2}>
			<BoxedSliderField
				label={t(R.paceSliderLabel)}
				value={context.monthlySaving}
				onChange={onChange}
				min={EARLY_FREEDOM_SAVING_MIN}
				max={EARLY_FREEDOM_SAVING_MAX}
				step={EARLY_FREEDOM_SAVING_STEP}
			/>

			<Stack direction="row" gap={0.8}>
				{context.scenarios.map((scenario) => (
					<EarlyFreedomScenarioCard
						key={scenario.key}
						label={t(EARLY_FREEDOM_SCENARIO_LABELS[scenario.key])}
						rate={`${getEarlyFreedomScenarioRatePercent(scenario)} % ${t(R.perYear)}`}
						years={formatYears(scenario.years)}
						age={
							scenario.targetAge !== null &&
							scenario.years !== null &&
							scenario.years > 0
								? t(R.ageLabel, { age: scenario.targetAge })
								: undefined
						}
						selected={scenario.key === selectedScenario}
						onClick={() => onSelectScenario(scenario.key)}
					/>
				))}
			</Stack>

			<Typography variant="hint" color={theme.palette.grey[500]}>
				{t(R.paceNote)}
			</Typography>
		</InspirationSection>
	);
};
